import {
  getStorefrontSiteConfig,
  resolveStorefrontSiteCode,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type { StorefrontPageProps } from "@/lib/storefront-shell-types";
import type { ProductListItem } from "@/lib/storefront-types";

const DEFAULT_GTM_ID = "GTM-57SL4VN";

export type AnalyticsSource = "web" | "app-webview";

export type AnalyticsContext = {
  brand: StorefrontSiteCode;
  checkout_mode: "localDelivery" | "parcelDelivery";
  source: AnalyticsSource;
  storefront_site_version?: string;
};

export type AnalyticsItem = {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category?: string;
  item_list_id?: string;
  item_list_name?: string;
  price: number;
  quantity?: number;
};

declare global {
  interface Window {
    __RL_ANALYTICS_CONTEXT?: Partial<AnalyticsContext>;
  }
}

export function isAnalyticsEnabled(value = process.env.NEXT_PUBLIC_ANALYTICS_ENABLED) {
  const normalizedValue = value?.trim().toLowerCase();
  return normalizedValue === "true" || normalizedValue === "1" || normalizedValue === "yes";
}

export function getGoogleTagManagerId() {
  return process.env.NEXT_PUBLIC_GTM_ID?.trim() || DEFAULT_GTM_ID;
}

export function getGoogleTagManagerNoScriptSrc(gtmId = getGoogleTagManagerId()) {
  return `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
}

export function resolveAnalyticsSiteCode(
  pageProps?: Partial<StorefrontPageProps> | null,
  hostname?: string | null,
) {
  return resolveStorefrontSiteCode({
    configuredSiteCode:
      pageProps?.siteCode ??
      process.env.NEXT_PUBLIC_STOREFRONT_SITE ??
      (typeof window === "undefined" ? process.env.STOREFRONT_SITE : null),
    hostname,
  });
}

export function resolveAnalyticsSource(pathname?: string | null): AnalyticsSource {
  if (pathname?.startsWith("/app/checkout")) {
    return "app-webview";
  }

  if (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("source") === "app-webview"
  ) {
    return "app-webview";
  }

  if (
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-rl-mobile-app-checkout") === "true"
  ) {
    return "app-webview";
  }

  return "web";
}

export function buildAnalyticsContext(
  siteCode: StorefrontSiteCode,
  source: AnalyticsSource,
): AnalyticsContext {
  const site = getStorefrontSiteConfig(siteCode);
  const version =
    process.env.NEXT_PUBLIC_STOREFRONT_SITE_VERSION?.trim() ||
    process.env.NEXT_PUBLIC_BUGSNAG_APP_VERSION?.trim() ||
    process.env.NEXT_PUBLIC_APP_VERSION?.trim();

  return removeUndefinedValues({
    brand: site.code,
    checkout_mode: site.checkoutMode,
    source,
    storefront_site_version: version,
  });
}

export function setAnalyticsContext(context: AnalyticsContext) {
  if (typeof window === "undefined") {
    return;
  }

  window.__RL_ANALYTICS_CONTEXT = removeUndefinedValues(context);
}

export function buildAnalyticsItemFromProduct(
  product: ProductListItem,
  options: {
    itemListId?: string;
    itemListName?: string;
    quantity?: number;
  } = {},
): AnalyticsItem {
  return removeUndefinedValues({
    item_id: product.key || product.id || product.snipcart.itemId || product.name,
    item_name: product.name,
    item_brand: resolveAnalyticsProductBrand(),
    item_category: product.category.name || undefined,
    item_list_id: options.itemListId,
    item_list_name: options.itemListName,
    price: product.price,
    quantity: options.quantity,
  });
}

export function pushDataLayerEvent(
  event: string,
  parameters: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event,
    ...(window.__RL_ANALYTICS_CONTEXT ?? {}),
    ...parameters,
  });
}

function resolveAnalyticsProductBrand() {
  return resolveStorefrontSiteCode({
    configuredSiteCode:
      process.env.NEXT_PUBLIC_STOREFRONT_SITE ??
      (typeof window === "undefined" ? process.env.STOREFRONT_SITE : null),
    hostname: typeof window === "undefined" ? null : window.location.host,
  });
}

function removeUndefinedValues<T extends Record<string, unknown>>(value: T) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined),
  ) as T;
}
